import React, { useEffect, useState } from 'react';
import { processTreeViewItems } from '@progress/kendo-react-treeview';
import {
  KendoReactTreeView,
  TreeViewExpandChangeEvent,
  TreeViewItemClickEvent
} from '@Shared.Angular/kendo.react/KendoReactTreeView';
import { CustomTreeListItem } from './CustomTreeListItem';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';

type Props = {
  categories: ITreeCategory<any>[];
  categoryClicked(
    id: string,
    index: string,
    name: string,
    expanded: IExpandedCategoryInfo
  ): void;
  expandedCategoryInfo?: IExpandedCategoryInfo;
  selectedCategoryId?: string;
};

const CategoryNavigation = (props: Props) => {
  const {
    categories,
    categoryClicked,
    selectedCategoryId,
    expandedCategoryInfo
  } = props;

  const [expandedCategories, setExpandedCategories] = useState({
    ids: [],
    idField: 'id'
  });
  // We shouldn't need currrentSelectedCategoryId at all, but the angular components
  // using this can't pass in updated selectedCategoryId,
  // because doing so recreates the entire CategoryNavigation component
  // and causes it to lose its scroll position and flicker.
  // TODO remove this once no angular components are using CategoryNavigation.
  const [currrentSelectedCategoryId, setCurrentSelectedCategoryId] = useState<
    string | null
  >(selectedCategoryId || (categories && categories[0]?.id) || null);

  useEffect(() => {
    if (
      expandedCategoryInfo !== null &&
      expandedCategoryInfo?.ids?.length > 0
    ) {
      setExpandedCategories((prevState) => ({
        ...prevState,
        ids: expandedCategoryInfo.ids
      }));
    }
  }, [expandedCategoryInfo]);

  useEffect(() => {
    let selectedId = selectedCategoryId || null;
    if (
      currrentSelectedCategoryId === null &&
      categories &&
      categories.length > 0
    ) {
      selectedId = categories[0].id;
      setCurrentSelectedCategoryId(selectedId);
    }

    if (selectedId !== null && !expandedCategories?.ids?.includes(selectedId)) {
      const expandedIds: string[] = [];
      const findAndExpand = (category: ITreeCategory<any>) => {
        if (category.id === selectedId) {
          expandedIds.push(category.id);
          return true;
        }
        if (category.items && category.items.length > 0) {
          for (const child of category.items) {
            if (findAndExpand(child)) {
              expandedIds.push(category.id);
              return true;
            }
          }
        }
        return false;
      };
      for (const category of categories) {
        if (findAndExpand(category)) {
          setExpandedCategories({ ids: expandedIds, idField: 'id' });
          break;
        }
      }
    }
  }, [categories, currrentSelectedCategoryId]);

  const handleItemClick = (event: TreeViewItemClickEvent) => {
    setCurrentSelectedCategoryId(event.item.id);
    categoryClicked(
      event.item.id,
      event.itemHierarchicalIndex,
      null,
      expandedCategories
    );
  };

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    const ids = expandedCategories.ids.slice();
    const category: ITreeCategory<any> = event.item;
    const index = ids.indexOf(category.id);

    if (index === -1) {
      ids.push(category.id);
      expandAllChildren(category, ids);
    } else {
      ids.splice(index, 1);
    }

    setExpandedCategories({ ids, idField: 'id' });
  };

  const expandAllChildren = (
    category: ITreeCategory<any>,
    expandedIds: string[]
  ) => {
    if (category.items && category.items.length > 0) {
      category.items.forEach((child) => {
        expandedIds.push(child.id);
        expandAllChildren(child, expandedIds);
      });
    }
  };

  return (
    <div className="category-navigation">
      <KendoReactTreeView
        data={processTreeViewItems(categories, {
          select: {
            idField: 'id',
            ids: [currrentSelectedCategoryId]
          },
          expand: expandedCategories
        })}
        item={(props) => (
          <CustomTreeListItem
            {...props}
            isExpanded={expandedCategories.ids.includes(props.item.id)}
          />
        )}
        expandIcons={true}
        onExpandChange={onExpandChange}
        onItemClick={handleItemClick}
      />
    </div>
  );
};

export default CategoryNavigation;
export interface IExpandedCategoryInfo {
  ids: string[];
  idField: string;
}
